import { useEffect, useState, useRef } from "react";
import { Gauge, ChevronDown, ChevronUp, Filter, X } from "lucide-react";
import api from "../../lib/api";
import ScorecardInfoTip from "./ScorecardInfoTip";
import MoMDeltaPill from "./MoMDeltaPill";

/**
 * Digital Property Optimization Score — Scorecard KPI (Roman + Yoquelvis).
 *
 * Composite of PageSpeed Performance (40%) + Core Web Vitals Pass (30%)
 * + Search CTR (15%) + Search Position (15%) across all SDM domains.
 *
 * Data source: `GET /api/scorecard/ga4/digital-property` — cached 24h.
 * Multi-site filter recomputes the score instantly from cached data.
 */
const fmtInt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));

const GRADE_META = {
  excellent: { label: "Excelente", color: "#16a34a" },
  good:      { label: "Bueno",     color: "#0ea5e9" },
  warning:   { label: "Alerta",    color: "#f97316" },
  poor:      { label: "Crítico",   color: "#dc2626" },
};

const CWV_COLOR = { FAST: "#16a34a", AVERAGE: "#f59e0b", SLOW: "#dc2626" };

// Extracted to module scope so React doesn't recreate the type on every
// parent render (would blow away the subtree state + trigger a lint err).
function CwvChip({ metric, value_ms, value_raw }) {
  const cat = metric?.category;
  const bg  = CWV_COLOR[cat] || "#94a3b8";
  const label = !cat ? "N/D" :
                cat === "FAST"    ? "OK"  :
                cat === "AVERAGE" ? "MED" : "SLOW";
  const tip = !cat
    ? "Sin field-data en CrUX (poco tráfico o < 28d de datos)"
    : `${cat} · ${value_ms != null ? Math.round(value_ms) + "ms" :
                  value_raw != null ? value_raw.toFixed(3) : "sin valor"}`;
  return (
    <span
      title={tip}
      className="text-[9px] font-extrabold px-1 py-0.5 rounded text-center block"
      style={{
        background: cat ? `${bg}20` : "#f1f5f9",
        color: cat ? bg : "#94a3b8",
        border: `1px solid ${cat ? bg + "40" : "#e2e8f0"}`,
      }}>
      {label}
    </span>
  );
}

export default function DigitalPropertyScoreCard({ unit, dateFrom, dateTo }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  // Multi-select domain filter — [] means "all sites"; recomputes score
  // from the cached per_property array on every change (no PSI re-hit).
  const [selectedSites, setSelectedSites] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Load roster once — populates the dropdown.
  useEffect(() => {
    let mounted = true;
    api.get("/scorecard/ga4/digital-property/domains")
      .then((r) => { if (mounted) setAvailableSites(r.data?.domains || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Close the filter popover on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    if (filterOpen) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [filterOpen]);

  useEffect(() => {
    let mounted = true;
    let pollTimer = null;
    setLoading(true);
    setError(null);
    const fetchOnce = async () => {
      try {
        const params = new URLSearchParams();
        if (unit)     params.append("unit", unit);
        // Propagate the FilterBar's date range so the GSC window
        // (clicks / CTR / position) reflects the exact period the user
        // is viewing, instead of the backend's rolling 28-day default.
        // PSI is always live-fetched so it's date-agnostic.
        if (dateFrom) params.append("date_from", dateFrom);
        if (dateTo)   params.append("date_to",   dateTo);
        // FastAPI collects repeated params into a list — one entry per site.
        for (const s of selectedSites) params.append("sites", s);
        const r = await api.get(`/scorecard/ga4/digital-property?${params.toString()}`);
        if (!mounted) return;
        setData(r.data);
        if (r.data?.status === "building") {
          pollTimer = setTimeout(fetchOnce, 30_000);
        }
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchOnce();
    return () => { mounted = false; if (pollTimer) clearTimeout(pollTimer); };
  }, [unit, selectedSites, dateFrom, dateTo]);

  const toggleSite = (name) => {
    setSelectedSites((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };
  const clearSites = () => setSelectedSites([]);

  if (loading && !data) {
    return (
      <section className="surface p-4" data-testid="dpo-score-loading">
        <div className="animate-pulse bg-slate-100" style={{ height: 220, borderRadius: 8 }}/>
      </section>
    );
  }
  if (error) {
    return (
      <section className="surface p-4" data-testid="dpo-score-error">
        <div className="text-xs font-bold text-red-600 mb-1">Digital Property Score</div>
        <div className="text-[11px] text-red-600">
          {error.includes("PAGESPEED_API_KEY")
            ? "Falta configurar PAGESPEED_API_KEY (Google Cloud → APIs → PageSpeed Insights)."
            : error.includes("Search Console")
            ? "Search Console pendiente: agregar el service account como usuario en cada propiedad."
            : `Error: ${error}`}
        </div>
      </section>
    );
  }
  if (!data) return null;

  // First-ever build in progress — friendly placeholder.
  if (data.status === "building") {
    return (
      <section className="surface p-4" data-testid="dpo-score-building">
        <div className="flex items-center gap-2 mb-3">
          <div className="kpi-icon-clean animate-pulse"
            style={{ width: 32, height: 32, color: "#6366f1",
              boxShadow: "inset 0 0 0 1px #6366f133" }}>
            <Gauge size={16}/>
          </div>
          <div className="flex-1">
            <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">
              Digital Property Optimization Score
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Generando primer snapshot ({data.properties_checked} dominios · PSI + GSC)
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="animate-pulse bg-slate-100" style={{ width: 100, height: 40, borderRadius: 6 }}/>
          <div className="text-[11px] text-gray-500">
            PageSpeed tarda ~30 s por dominio.<br/>
            Vuelvo a chequear cada 30 s automáticamente.
          </div>
        </div>
      </section>
    );
  }

  const score = data.digital_property_optimization_score || 0;
  const grade = GRADE_META[data.grade] || GRADE_META.warning;
  const k     = data.kpis || {};
  const sc    = data.score_components || {};

  const bars = [
    { key: "performance",     label: "PageSpeed Perf",    weight: "40%",
      value: sc.performance_score, tint: "#0ea5e9",
      sub: `Lighthouse mobile avg — ${data.properties_psi_ok}/${data.properties_checked} sitios` },
    { key: "cwv",             label: "Core Web Vitals",   weight: "30%",
      value: sc.cwv_score, tint: "#8b5cf6",
      sub: `${k.cwv_measurable || 0} sitios con field-data · % en FAST` },
    { key: "ctr",             label: "Search CTR",        weight: "15%",
      value: sc.ctr_score, tint: "#14b8a6",
      sub: `${(k.search_ctr_pct || 0).toFixed(2)}% CTR · ${fmtInt(k.search_total_impressions)} impresiones` },
    { key: "position",        label: "Search Position",   weight: "15%",
      value: sc.position_score, tint: "#f59e0b",
      sub: `Pos. promedio ${(k.search_position_avg || 0).toFixed(1)}` },
  ];

  return (
    <section className="surface p-4" data-testid="dpo-score-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="kpi-icon-clean" style={{
          width: 32, height: 32, color: "#6366f1",
          boxShadow: "inset 0 0 0 1px #6366f133",
        }}>
          <Gauge size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
            Digital Property Optimization Score
            <ScorecardInfoTip
              label="Digital Property Optimization Score"
              formula="Composite 0-100: PageSpeed Perf 40% + CWV Pass 30% + Search CTR 15% + Search Position 15%"
              extra={`Roman + Yoquelvis · Scorecard Jul 2026. PSI = Lighthouse mobile score; CWV = % de sitios con LCP+INP+CLS en FAST; SEO ponderado por impresiones. Cacheado 24h.`}
              source={`PageSpeed Insights + Search Console · ${data.properties_checked} dominios`}
            />
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {data.period?.gsc_start} → {data.period?.gsc_end} · {fmtInt(k.search_total_clicks)} clicks orgánicos
          </div>
        </div>
        {/* Multi-select site filter — recomputes score from cache. */}
        <div ref={filterRef} className="relative flex-shrink-0">
          <button
            data-testid="dpo-sites-filter-btn"
            onClick={() => setFilterOpen((v) => !v)}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold tabular-nums transition-colors"
            style={{
              background: selectedSites.length ? "#6366f11a" : "#f1f5f9",
              color:      selectedSites.length ? "#6366f1"   : "#475569",
              boxShadow:  selectedSites.length ? "inset 0 0 0 1px #6366f166" : "none",
            }}>
            <Filter size={11}/>
            {selectedSites.length === 0
              ? "Todos"
              : `${selectedSites.length} sitio${selectedSites.length > 1 ? 's' : ''}`}
            <ChevronDown size={10}/>
          </button>
          {filterOpen && (
            <div
              data-testid="dpo-sites-filter-menu"
              className="absolute right-0 top-full mt-1 z-20 rounded-lg overflow-hidden"
              style={{
                background: "#fff", minWidth: 220,
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.15), 0 0 0 1px #e2e8f0",
              }}>
              <div className="px-3 py-2 flex items-center justify-between"
                style={{ borderBottom: "1px solid #f1f5f9" }}>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
                  Filtrar por sitio
                </span>
                {selectedSites.length > 0 && (
                  <button
                    data-testid="dpo-sites-clear"
                    onClick={clearSites}
                    className="text-[9px] font-bold text-red-600 hover:underline flex items-center gap-0.5">
                    <X size={9}/> Limpiar
                  </button>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {availableSites.map((s) => {
                  const active = selectedSites.includes(s.name);
                  return (
                    <label
                      key={s.name}
                      data-testid={`dpo-site-toggle-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                      className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50"
                      style={{ userSelect: "none" }}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleSite(s.name)}
                        className="h-3 w-3"
                      />
                      <span className="text-[11px] font-bold text-gray-700 flex-1">{s.name}</span>
                      <span className="text-[9px] font-semibold text-gray-400">{s.unit}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Score headline */}
      <div className="flex items-baseline gap-2 mb-3"
        style={{ fontFamily: "Manrope, sans-serif" }}>
        <span className="font-extrabold tabular-nums"
          style={{ fontSize: 40, color: grade.color, letterSpacing: "-0.03em", lineHeight: 1 }}>
          {score.toFixed(1)}
        </span>
        <span className="text-sm font-bold text-gray-500">/ 100</span>
        <MoMDeltaPill metric="dpo" />
        <span className="ml-auto text-[10px] font-bold uppercase tabular-nums px-2 py-0.5 rounded"
          style={{ background: `${grade.color}18`, color: grade.color }}>
          {grade.label}
        </span>
      </div>

      {/* Component bars */}
      <div className="space-y-2">
        {bars.map((b) => (
          <div key={b.key} data-testid={`dpo-comp-${b.key}`}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-bold text-gray-700">{b.label}</span>
                <span className="text-[9px] font-semibold text-gray-400">· {b.weight}</span>
              </div>
              <span className="tabular-nums font-extrabold text-[11px]" style={{ color: b.tint }}>
                {(b.value || 0).toFixed(1)}
              </span>
            </div>
            <div style={{ height: 6, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${Math.min(100, b.value || 0)}%`,
                background: b.tint,
                transition: "width 0.4s ease",
              }}/>
            </div>
            <div className="text-[9px] text-gray-500 mt-0.5">{b.sub}</div>
          </div>
        ))}
      </div>

      {/* Per-property drill-down */}
      {data.per_property?.length > 0 && (
        <div className="mt-3 pt-2" style={{ borderTop: "1px solid #f1f5f9" }}>
          <button
            data-testid="dpo-details-toggle"
            onClick={() => setShowDetails((v) => !v)}
            className="w-full flex items-center justify-between text-[10px] font-bold text-gray-500 hover:text-gray-700">
            <span>Detalle por dominio ({data.per_property.length}) — CWV + PSI + GSC</span>
            {showDetails ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
          </button>
          {showDetails && (
            <div className="mt-2">
              {/* Header row */}
              <div className="grid text-[9px] font-extrabold uppercase tracking-wider text-gray-400 pb-1 pt-1"
                   style={{ gridTemplateColumns: "1.5fr 42px 46px 46px 46px 60px", gap: 4,
                            borderBottom: "1px solid #f1f5f9" }}>
                <span>Dominio</span>
                <span className="text-center" title="PageSpeed Performance (0-100)">PSI</span>
                <span className="text-center" title="Largest Contentful Paint">LCP</span>
                <span className="text-center" title="Interaction to Next Paint">INP</span>
                <span className="text-center" title="Cumulative Layout Shift">CLS</span>
                <span className="text-right" title="Clicks orgánicos GSC (28d)">Clicks</span>
              </div>
              {data.per_property.map((p) => {
                const perf   = p.psi?.performance_score ?? null;
                const clicks = p.gsc?.clicks ?? null;
                const cwvErr = p.psi?.error;
                const gscErr = p.gsc?.error;
                const lcp    = p.psi?.lcp || {};
                const inp    = p.psi?.inp || {};
                const cls    = p.psi?.cls || {};
                return (
                  <div key={p.name}
                       className="grid items-center text-[10px] py-1.5"
                       style={{ gridTemplateColumns: "1.5fr 42px 46px 46px 46px 60px", gap: 4,
                                borderBottom: "1px dashed #f1f5f9" }}
                       data-testid={`dpo-domain-${p.name.replace(/\s+/g, '-').toLowerCase()}`}>
                    <span className="font-bold text-gray-700 truncate" title={p.name}>{p.name}</span>
                    {/* PSI perf score */}
                    <span className="text-center font-extrabold tabular-nums" style={{
                      color: cwvErr ? "#dc2626"
                        : perf >= 80 ? "#16a34a"
                        : perf >= 50 ? "#f59e0b" : "#dc2626",
                    }} title={cwvErr || `PageSpeed Performance ${perf?.toFixed(0) ?? "—"}`}>
                      {cwvErr ? "!" : (perf != null ? perf.toFixed(0) : "—")}
                    </span>
                    {/* LCP / INP / CLS chips — value units differ (ms vs unitless CLS). */}
                    <CwvChip metric={lcp} value_ms={lcp.value_ms} />
                    <CwvChip metric={inp} value_ms={inp.value_ms} />
                    <CwvChip metric={cls} value_raw={cls.value} />
                    {/* Clicks orgánicos */}
                    <span className="tabular-nums text-gray-500 text-right" title={gscErr || undefined}>
                      {gscErr ? "GSC!" : fmtInt(clicks)}
                    </span>
                  </div>
                );
              })}
              <div className="mt-2 flex items-center gap-3 text-[9px] text-gray-400">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded" style={{ background: "#16a34a" }}/> OK (FAST)
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded" style={{ background: "#f59e0b" }}/> MED (AVERAGE)
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded" style={{ background: "#dc2626" }}/> SLOW
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded" style={{ background: "#e2e8f0" }}/> N/D (sin field-data)
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
